import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { outfitService, OutfitShare } from '../services/outfit';
import { Button } from '../components/common/Button';
import { getImageUrl } from '../utils/imageUrl';

export const Shares: React.FC = () => {
  const navigate = useNavigate();
  const [receivedShares, setReceivedShares] = useState<OutfitShare[]>([]);
  const [sentShares, setSentShares] = useState<OutfitShare[]>([]);
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadShares();
  }, []);

  const loadShares = async () => {
    try {
      const [received, sent] = await Promise.all([
        outfitService.getReceivedShares(),
        outfitService.getSentShares(),
      ]);
      setReceivedShares(received);
      setSentShares(sent);
    } catch (err) {
      console.error('Failed to load shares:', err);
    } finally {
      setLoading(false);
    }
  };

  const shares = activeTab === 'received' ? receivedShares : sentShares;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-2xl font-bold text-gray-900">Shares</h1>
            <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')}>
              Closet
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('received')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'received'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Received ({receivedShares.length})
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'sent'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sent ({sentShares.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : shares.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {activeTab === 'received'
                ? "You haven't received any outfit shares yet!"
                : "You haven't sent any outfit shares yet!"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shares.map((share) => (
              <div
                key={share.id}
                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow"
              >
                {share.outfit && (
                  <>
                    <div className="p-4">
                      <h3 className="text-lg font-bold mb-2">{share.outfit.name}</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {activeTab === 'received'
                          ? `From: ${share.sender?.username || 'Unknown'}`
                          : `To: ${share.recipient?.username || 'Unknown'}`}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {share.outfit.items.slice(0, 4).map((item) => (
                          <div key={item.id} className="aspect-square rounded-xl overflow-hidden">
                            <img
                              src={getImageUrl(item.image_url)}
                              alt={item.category}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      {activeTab === 'received' && share.status === 'pending' && (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full"
                          onClick={() => navigate(`/shares/${share.id}`)}
                        >
                          View & Respond
                        </Button>
                      )}
                      {share.feedback_type && (
                        <div className="mt-2">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                              share.feedback_type === 'like'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {share.feedback_type === 'like' ? '👍 Liked' : '👎 Disliked'}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

